import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, X, GripVertical } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export interface StoryBeat {
  id: string;
  title: string;
  description: string;
}

interface PlanBeatsStepProps {
  beats: StoryBeat[];
  onBeatsChange: (beats: StoryBeat[]) => void;
}

export function PlanBeatsStep({
  beats,
  onBeatsChange,
}: PlanBeatsStepProps) {
  const handleAddBeat = () => {
    const newBeat: StoryBeat = {
      id: crypto.randomUUID(),
      title: `Plot Point ${beats.length + 1}`,
      description: '',
    };
    onBeatsChange([...beats, newBeat]);
  };

  const handleDeleteBeat = (id: string) => {
    onBeatsChange(beats.filter(b => b.id !== id));
  };

  const handleUpdateBeat = (id: string, field: 'title' | 'description', value: string) => {
    onBeatsChange(
      beats.map(b => b.id === id ? { ...b, [field]: value } : b)
    );
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-700">
      {/* Step Header matching Step 0 */}
      <div className="space-y-3">
        <h1 className="text-3xl font-bold tracking-tight text-foreground font-lora">
          Plan Your Beats
        </h1>
        <p className="text-muted-foreground text-sm leading-relaxed max-w-md">
          Outline the major turning points of your story. You can refine and reorder these later in your timeline.
        </p>
      </div>

      <div className="space-y-4 max-h-[450px] overflow-y-auto pr-3 -mr-3 custom-scrollbar">
        <AnimatePresence mode="popLayout">
          {beats.map((beat, index) => (
            <motion.div
              key={beat.id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="group relative"
            >
              <div className="flex gap-4 p-4 rounded-xl bg-muted/5 border border-border/40 hover:border-primary/20 transition-all">
                <div className="flex flex-col items-center pt-1 opacity-20 group-hover:opacity-40 transition-opacity">
                  <GripVertical className="h-4 w-4 cursor-grab active:cursor-grabbing" />
                  <span className="text-[10px] font-black mt-2 font-sans">{index + 1}</span>
                </div>
                
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between gap-4">
                    <Input
                      value={beat.title}
                      onChange={(e) => handleUpdateBeat(beat.id, 'title', e.target.value)}
                      placeholder="Scene Title (e.g. The Meeting)"
                      className="h-7 border-none bg-transparent p-0 font-bold text-base focus-visible:ring-0 shadow-none placeholder:text-muted-foreground/20"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteBeat(beat.id)}
                      className="h-7 w-7 rounded-full text-muted-foreground/30 hover:text-destructive hover:bg-destructive/5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  
                  <Textarea
                    value={beat.description}
                    onChange={(e) => handleUpdateBeat(beat.id, 'description', e.target.value)}
                    placeholder="Describe what happens..."
                    className="min-h-[60px] text-sm border-none bg-transparent p-0 focus-visible:ring-0 resize-none shadow-none leading-relaxed placeholder:text-muted-foreground/20"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        <Button
          onClick={handleAddBeat}
          variant="ghost"
          className="w-full py-8 border-dashed border-2 rounded-xl bg-muted/5 border-border/40 hover:bg-primary/[0.02] hover:border-primary/30 text-muted-foreground/40 hover:text-primary/60 transition-all gap-2 text-xs font-bold uppercase tracking-widest"
        >
          <Plus className="h-4 w-4" />
          Add Story Beat
        </Button>
      </div>
    </div>
  );
}
